/* eslint-disable react-hooks/exhaustive-deps */
import * as React from 'react';
import { styled } from '@mui/material/styles';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Collapse from '@mui/material/Collapse';
import IconButton, { IconButtonProps } from '@mui/material/IconButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Button } from 'react-bootstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import DatePickerInput from 'components/input/DatePicker';
import SelectInput from 'components/input/SelectInput';
import { formatDate } from 'components/helper/helper';
import moment from 'moment';
import GroupRadioButtonRounded from 'components/input/GroupRadioButtonRounded';
import YesNoSwitch from 'components/input/YesNoSwitch';
import {
  ibSubjectOption,
  ucibSubjectOption,
  ibSubjectHaveLanguageQuestion,
  csu_ibSubjectOption,
} from 'components/helper/appConfig';
import GroupRadioButton from 'components/input/GroupRadioButton';
import GroupCheckbox from 'components/input/GroupCheckbox';
import TextInput from 'components/input/textInput';
import Grid from '@mui/joy/Grid';
import { debounce } from 'lodash';

interface ExpandMoreProps extends IconButtonProps {
  expand: boolean;
}

const ExpandMore = styled((props: ExpandMoreProps) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

interface Props {
  handleSave: (value: any) => void;
  item: any;
  index: any;
  indexText: string;
  check?: any;
  indexExpanded?: any;
  expandAll?: boolean;
  handleExpandActive: (value: any) => void;
  isMobile?: boolean;
  disabled?: boolean;
}

const IBSubjectConfig = (props: Props) => {
  const { check, isMobile = false, disabled = false } = props;
  const validationSchema = Yup.object().shape({
    dateTaken: Yup.string().nullable().required('Date is required')
        .test(
          'is-valid-date',
          'Invalid date',
          function (value:any) {
            if (!value) {
              return false;
            }

            let year = moment(value).format('yyyy');
            if (Number(year)<2000) return false;

            return moment(value).isValid();
          }
        ),
    ibExamSubjectNameDescribeOther: Yup.string().nullable()
    .when('ibExamName', {
        is: 'Other',
        then: Yup.string().nullable().required('Field is required'),
    }),

  });

  const formItem = useFormik({
    initialValues: {
      isReceivedScore: '',
      examStatus: 'Planned',
      subject: '',
      dateTaken: null,
      score: '',
      ibExam: '',
      ibExamName: '', // for UC App only
      level: '',
      ibExamSubjectName: '', // for UC App only
      ibExamSubjectNameDescribeOther: '', // for UC App only
      ibExamSubjectNameCSUApp: '', // for CSU App only
      ibExamSpecifyLanguageCSUApp: '', // for CSU App only
      language: '',
      isIncludeIntoCommonApp: false,
      isIncludeIntoUCApp: false,
      isIncludeIntoCSUApp: false,
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      props.handleSave({ ...values });
    },
  });

  const [expanded, setExpanded] = React.useState(props.expandAll || false);
  const handleExpandClick = () => {
    setExpanded(!expanded);
    props.handleExpandActive(expanded);
  };

  const elementRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (check && props.indexExpanded === props.indexText) {
      setExpanded(true);

      setTimeout(() => {
        if (elementRef.current) {
          elementRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }, 100);
    } else {
      setExpanded(props.expandAll || false);
    }

    if (props.item) formItem.setValues({ ...formItem.values, ...props.item });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props?.expandAll]);

  const [ucibExcamOption, setUcibExcamOption] = React.useState<string[]>([]);
  
  // Create custom subject options that include current value if not in ibSubjectOption
  const customSubjectOptions = React.useMemo(() => {
    if (formItem.values.subject && !ibSubjectOption.includes(formItem.values.subject)) {
      return [...ibSubjectOption, formItem.values.subject];
    }
    return ibSubjectOption;
  }, [formItem.values.subject]);

  React.useEffect(() => {
    let optionData = ucibSubjectOption.find((e) => e.value === formItem.values.ibExamSubjectName);
    let examOption =
      formItem.values.level === 'Higher Level (HL)'
        ? optionData?.optionHL || []
        : optionData?.optionSL || [];
    setUcibExcamOption(examOption);
    if (!examOption.includes(formItem.values.ibExamName) && formItem.dirty) {
      formItem.setFieldValue('ibExamName', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formItem.values.ibExamSubjectName, formItem.values.level]);


  React.useEffect(() => {
    if (formItem.dirty) {
      props.handleSave({ ...formItem.values });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formItem.values]);
  
  //#region Auto save function ==========
  React.useEffect(() => {
    formItem.dirty && debounceSaving();
  }, [formItem.values]);
  const debounceSaving = React.useCallback(
    debounce(() => {
      formItem.handleSubmit();
    }, 1500),
    []
  );
  //#endregion ==========================
    
  const renderFillAppStatus = () => {
    return (
      <span className='mt-2' style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
        {formItem.values.isIncludeIntoCommonApp && (
          <img
            alt="cm"
            src="/assets/icons/icon-cm.svg"
            style={{ marginTop: '-8px', marginLeft: '4px' }}
          />
        )}
        {formItem.values.isIncludeIntoUCApp && (
          <img
            alt="uc"
            src="/assets/icons/icon-uc.svg"
            style={{ marginTop: '-8px', marginLeft: '4px' }}
          />
        )}
        {formItem.values.isIncludeIntoCSUApp && (
          <img
            alt="csu"
            src="./assets/icons/icon-csu-5.svg"
            style={{ marginTop: '-13px', marginLeft: '4px' }}
          />
        )}
      </span>
    );
  };

  const buildHeader = () => {
    let title = ``;

    if (formItem.values.subject) {
      title = `IB ${formItem.values.subject}`;
    }

    const details = [];

    if (formItem.values.dateTaken) {
      details.push(formatDate(formItem.values.dateTaken, 'MM/DD/YYYY'));
    }

    if (formItem.values.score) {
      details.push(`Score: ${formItem.values.score}`);
    }

    return !details.length ? title : `${title} - ${details.join(' - ')}`;
  };

  return (
    <Card className="w-100 my-3 px-0" key={props.indexText} ref={elementRef}>
      <CardActions
        disableSpacing
        onClick={handleExpandClick}
        sx={{
          backgroundColor: expanded ? '#AAEBF7' : 'none',
          borderBottom: expanded ? '2px solid #4D88F0' : 'none',
          marginLeft: '-8px',
        }}
      >
        <div className="w-100 d-flex align-items-center">
          <span className={`p-1 ps-3 flex-grow-1`}>
            {buildHeader()}
          </span>
          {renderFillAppStatus()}
          <strong className="text-field-light mt-1 me-3" style={{ minWidth: '40px', textAlign: 'right' }}>{props.indexText}</strong>
        </div>
        <ExpandMore
          expand={expanded}
          onClick={handleExpandClick}
          aria-expanded={expanded}
          aria-label="show more"
          id={`btn_fold_${props.indexText.replace("/","_")}`}
        >
          <ExpandMoreIcon />
        </ExpandMore>
      </CardActions>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <CardContent>
        <form id={`SubjectConfig_${props.index}`} onSubmit={formItem.handleSubmit}>
          <Grid container spacing={2}>
            <Grid xs={12} md={6}>
              <GroupRadioButtonRounded
                id={`examStatus_${props.index+1}`}
                name="examStatus"
                label="Is this exam completed or planned?*"
                values={formItem.values.examStatus}
                onChange={formItem.handleChange}
                options={[{ value: 'Completed' }, { value: 'Planned' }]}
                isMissing={!formItem.values.examStatus && check}
                disabled={disabled}
              />
            </Grid>

            <Grid xs={12} md={6}>
              <YesNoSwitch
                hidden={formItem.values.examStatus === 'Planned'}
                id={`isReceivedScore_${props.index+1}`}
                className="mb-3"
                label="Have you received your score yet? *"
                name="isReceivedScore"
                values={formItem.values.isReceivedScore}
                isMissing={!formItem.values.isReceivedScore && check}
                onChange={(e: any) => {
                  formItem.handleChange(e);
                  if (e.target.value === 'false') {
                    formItem.setFieldValue('score', 'Score Not Yet Received');
                  }
                }}
                disabled={disabled}
              />
            </Grid>

            <Grid xs={12} md={6}>
              <SelectInput
                id={`subject_${props.index+1}`}
                form={formItem}
                option={customSubjectOptions}
                label="Subject*"
                isSatTest={true}
                name="subject"
                className="common-flag"
                values={formItem.values.subject}
                onChange={formItem.handleChange}
                logo="span-cm-logo"
                isMissing={!formItem.values.subject && check}
                disabled={disabled}
              />
            </Grid>

            <Grid xs={12} md={6}>
              <SelectInput
                id={`level_${props.index+1}`}
                logo="span-cm-logo"
                form={formItem}
                option={['Standard Level (SL)', 'Higher Level (HL)']}
                label="Level (select the one that applies)*"
                name="level"
                values={formItem.values.level}
                onChange={formItem.handleChange}
                isMissing={!formItem.values.level && check}
                disabled={disabled}
              />
            </Grid>

            <Grid xs={12}>
              <GroupRadioButton
                id={`language_${props.index+1}`}
                name="language"
                values={formItem.values.language}
                onClear={() => formItem.setFieldValue('language', '')}
                onChange={formItem.handleChange}
                options={[
                  { value: 'English' },
                  { value: 'French' },
                  { value: 'Portuguese' },
                  { value: 'Spanish' },
                  { value: 'Turkish' },
                ]}
                label="Language (select the one that applies)*"
                hidden={!ibSubjectHaveLanguageQuestion.includes(formItem.values.subject)}
                isMissing={!formItem.values.language && check}
                disabled={disabled}
              />
            </Grid>

            <Grid xs={12} md={6}>
              <SelectInput
                form={formItem}
                option={ucibSubjectOption}
                label="IB exam subject area"
                isSatTest={true}
                name="ibExamSubjectName"
                className="uc-flag"
                logo="span-uc-logo"
                values={formItem.values.ibExamSubjectName}
                onChange={formItem.handleChange}
                disabled={disabled}
              />
            </Grid>

            <Grid xs={12} md={6}>
              <SelectInput
                form={formItem}
                option={ucibExcamOption}
                logo="span-uc-logo"
                label="IB exam name *"
                isSatTest={true}
                name="ibExamName"
                className="uc-flag end-flag"
                values={formItem.values.ibExamName}
                onChange={formItem.handleChange}
                isMissing={!formItem.values.ibExamName && check}
                disabled={disabled}
              />
            </Grid>

            {formItem.values.ibExamName === 'Other' && (
              <Grid xs={12}>
                <>
                <TextInput
                  id={`ibExamSubjectNameDescribeOther_${props.index+1}`}
                  countText={30}
                  label="Describe other *"
                  className="uc-flag end-flag"
                  logo="span-uc-logo"
                  name="ibExamSubjectNameDescribeOther"
                  values={formItem.values.ibExamSubjectNameDescribeOther}
                  onChange={formItem.handleChange}
                  isMissing={!formItem.values.ibExamSubjectNameDescribeOther && check}
                  disabled={disabled}
                />
              {formItem.errors.ibExamSubjectNameDescribeOther && formItem.touched.ibExamSubjectNameDescribeOther && (
                  <div className="text-danger">{formItem.errors.ibExamSubjectNameDescribeOther}</div>
              )}
                </>
              </Grid>
            )}

            <Grid xs={12} md={6}>
              <SelectInput
                form={formItem}
                option={csu_ibSubjectOption}
                label="IB exam subject area"
                isSatTest={true}
                name="ibExamSubjectNameCSUApp"
                className="csu-flag"
                logo="span-csu-logo"
                values={formItem.values.ibExamSubjectNameCSUApp}
                onChange={formItem.handleChange}
                disabled={disabled}
              />
            </Grid>

            <Grid xs={12} md={6}>
              <TextInput
                countText={50}
                label="Specify Language"
                className="csu-flag"
                logo="span-csu-logo"
                name="ibExamSpecifyLanguageCSUApp"
                values={formItem.values.ibExamSpecifyLanguageCSUApp}
                onChange={formItem.handleChange}
                disabled={disabled}
              />
            </Grid>

            <Grid xs={12} md={6}>
              <>
              <DatePickerInput
                id={`dateTaken_${props.index+1}`}
                label={'Date taken or planned*'}
                name="dateTaken"
                values={formItem.values.dateTaken}
                onChange={(value) => formItem.setFieldValue('dateTaken', value)}
                isMissing={!formItem.values.dateTaken && check}
                disabled={disabled}
              />
                {formItem.errors.dateTaken && formItem.touched.dateTaken && (
                    <div className="text-danger">{formItem.errors.dateTaken}</div>
                )}
              </>
            </Grid>

            {formItem.values.isReceivedScore === 'true' && (
              <Grid xs={12} md={6}>
                <SelectInput
                  id={`score_${props.index+1}`}
                  form={formItem}
                  label="Score *"
                  name="score"
                  option={['1', '2', '3', '4', '5', '6', '7'].reverse()}
                  values={
                    formItem.values.score === 'Score Not Yet Received' ? '1' : formItem.values.score
                  }
                  onChange={formItem.handleChange}
                  isMissing={!formItem.values.score && check}
                  disabled={disabled}
                  />
              </Grid>
            )}

            {formItem.values.isReceivedScore === 'false' && (
              <Grid xs={12} md={6}>
                <SelectInput
                  id={`score_${props.index+1}`}
                  form={formItem}
                  className="csu-flag"
                  logo="span-csu-logo"
                  label="Score *"
                  name="score"
                  option={['Score Not Yet Received']}
                  values={formItem.values.score}
                  onChange={formItem.handleChange}
                  isMissing={formItem.values.score!=='Score Not Yet Received' && check}
                  disabled={disabled}
                />
              </Grid>
            )}

            <Grid xs={12}>
              <GroupCheckbox
                label="Include test info into these applications"
                form={formItem}
                mdCol={3}
                columns={3}
                options={[
                  {
                    value: 'isIncludeIntoCommonApp',
                    label: 'Common App',
                  },
                  {
                    value: 'isIncludeIntoUCApp',
                    label: 'UC App',
                  },
                  {
                    value: 'isIncludeIntoCSUApp',
                    label: 'CSU App',
                  },
                ]}
                disabled={disabled}
              />
            </Grid>

            <Grid xs={12} md={9}></Grid>
            <Grid xs={12} md={3}>
              <Button
                className="w-100 btn-save-info"
                onClick={() => {
                  formItem.handleSubmit();
                  if (formItem.isValid) {
                    setExpanded(false);
                  }
                }}
                disabled={disabled}
              >
                Save Info
              </Button>
            </Grid>
          </Grid>
        </form>
        </CardContent>

      </Collapse>
    </Card>
  );
};

export default IBSubjectConfig;
